type EpaycoCheckoutConfig = {
  sessionId: string
  type: 'onpage' | 'standard' | 'script'
  test: boolean
}

type EpaycoCheckoutInstance = {
  open: () => void
  onCreated?: (callback: () => void) => void
  onErrors?: (callback: (errors: unknown) => void) => void
  onClosed?: (callback: () => void) => void
}

type EpaycoCheckoutHandlers = {
  onCreated?: () => void
  onErrors?: (errors: unknown) => void
  onClosed?: () => void
}

type EpaycoWindow = Window & {
  ePayco?: {
    checkout: {
      configure: (config: EpaycoCheckoutConfig) => EpaycoCheckoutInstance
    }
  }
}

let epaycoScriptPromise: Promise<void> | null = null

const loadEpaycoScript = (scriptUrl = 'https://checkout.epayco.co/checkout-v2.js') => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('ePayco solo puede cargarse en el navegador'))
  }

  const currentWindow = window as EpaycoWindow
  if (currentWindow.ePayco?.checkout) {
    return Promise.resolve()
  }

  if (epaycoScriptPromise) {
    return epaycoScriptPromise
  }

  epaycoScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-epayco-checkout="true"]',
    )

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error('No se pudo cargar el script de ePayco')),
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.defer = true
    script.dataset.epaycoCheckout = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('No se pudo cargar el script de ePayco'))
    document.head.appendChild(script)
  }).catch((error) => {
    epaycoScriptPromise = null
    throw error
  })

  return epaycoScriptPromise
}

export const openEpaycoCheckout = async (
  config: EpaycoCheckoutConfig,
  handlers?: EpaycoCheckoutHandlers,
  scriptUrl?: string,
) => {
  await loadEpaycoScript(scriptUrl)

  const currentWindow = window as EpaycoWindow
  const checkoutFactory = currentWindow.ePayco?.checkout

  if (!checkoutFactory) {
    throw new Error('ePayco no esta disponible en esta pagina')
  }

  const checkout = checkoutFactory.configure({
    ...config,
    type: config.type === 'script' ? 'onpage' : config.type,
  })
  checkout.onCreated?.(() => handlers?.onCreated?.())
  checkout.onErrors?.((errors: unknown) => handlers?.onErrors?.(errors))
  checkout.onClosed?.(() => handlers?.onClosed?.())
  checkout.open()
}
